import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/core/AuthContext';
import { SectionCard } from '../../components/trainer/SectionCard';
import { StatPill } from '../../components/trainer/StatPill';
import { ClientsPanel } from '../../components/trainer/ClientsPanel';
import { TrainerSessionsList } from '../../components/trainer/TrainerSessionsList';
import { SessionReviewPanel } from '../../components/trainer/SessionReviewPanel';
import { useTrainerClients } from '../../lib/useTrainerClients';
import { useTrainerTemplates } from '../../lib/useTrainerTemplates';
import { useTrainerSessions } from '../../lib/useTrainerSessions';
import { useIsMobile } from '../../lib/useIsMobile';
import { useToast } from '@/core/ToastContext';
import { buildTrainerInviteUrl } from '../../lib/trainerInvite';

export function Clients() {
  const { userData, user } = useAuth();
  const { success, error: showError } = useToast();
  const trainerId = useMemo(
    () => userData?.trainerProfileId || userData?.trainerId || user?.uid,
    [userData, user]
  );
  const [reviewSessionId, setReviewSessionId] = useState<string | null>(null);
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { clients, addClient, error } = useTrainerClients(trainerId);
  const { assignments } = useTrainerTemplates(trainerId);
  const { sessions } = useTrainerSessions(trainerId);

  const copyInviteLink = async () => {
    if (!trainerId) {
      showError('Missing trainer ID')
      return
    }

    const inviteUrl = buildTrainerInviteUrl(trainerId)

    try {
      await navigator.clipboard.writeText(inviteUrl)
      success('Invite link copied')
    } catch (copyError) {
      console.error('Failed to copy invite link:', copyError)
      showError('Could not copy invite link')
    }
  }

  // Client name map for sessions
  const clientNameMap = useMemo(() => {
    const map = new Map<string, string>();
    clients.forEach((client) => {
      map.set(client.id, client.displayName || client.phone || 'Unknown client');
    });
    return map;
  }, [clients]);

  // Session stats
  const recentSessions = sessions.slice(0, 8);
  const inProgressSessions = sessions.filter(s => s.status === 'in_progress');
  const thisWeekSessions = sessions.filter(s => {
    const startOfWeek = new Date();
    startOfWeek.setHours(0, 0, 0, 0);
    startOfWeek.setDate(startOfWeek.getDate() - startOfWeek.getDay());
    const sessionDate = s.startedAt.toDate();
    return sessionDate >= startOfWeek;
  });

  if (!trainerId) {
    return (
      <div className="p-6">
        <div className="max-w-7xl mx-auto">
          <div className="glass rounded-2xl p-8 text-center">
            <p className="text-lg font-semibold mb-2 text-gray-900 dark:text-white">
              Trainer not linked
            </p>
            <p className="text-sm text-gray-600 dark:text-slate-400">
              Your account is missing a trainerId claim.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
            <div>
              <p className="text-sm text-primary-600 dark:text-primary-400 uppercase tracking-wide font-semibold">
                Clients & Activity
              </p>
              <h1 className="text-3xl font-semibold text-gray-900 dark:text-white">
                Your Clients
              </h1>
              <p className="text-sm text-gray-600 dark:text-slate-400">
                Manage clients and review recent sessions
              </p>
            </div>
            <button
              type="button"
              onClick={() => void copyInviteLink()}
              className="inline-flex items-center justify-center rounded-xl bg-gray-900 px-4 py-2 text-sm font-semibold text-white transition hover:bg-gray-800 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100"
            >
              Copy Invite Link
            </button>
          </div>
        </div>

        {/* Stats */}
        {!isMobile && (
        <div className="grid sm:grid-cols-5 gap-3">
          <StatPill label="Clients" value={clients.length} />
          <StatPill label="Assignments" value={assignments.length} />
          <StatPill label="Sessions" value={sessions.length} />
          <StatPill label="This Week" value={thisWeekSessions.length} />
          <StatPill label="In Progress" value={inProgressSessions.length} />
        </div>
        )}
        {/* Main Clients Panel */}
        <ClientsPanel
          clients={clients}
          assignments={assignments}
          sessions={sessions}
          onAddClient={addClient}
          onSelectClient={(id) => navigate(`/trainer/clients/${id}`)}
          error={error}
        />

        {/* Recent Activity */}
        <SectionCard
          eyebrow="Activity"
          title="Recent Sessions"
          action={
            recentSessions.length > 0 && (
              <span className="text-xs text-gray-600 dark:text-slate-400">
                {sessions.length} total
              </span>
            )
          }
        >
          {recentSessions.length > 0 ? (
            <TrainerSessionsList
              sessions={recentSessions}
              clientNameMap={clientNameMap}
              onSessionClick={setReviewSessionId}
            />
          ) : (
            <p className="text-sm text-gray-600 dark:text-slate-400 py-4 text-center">
              No sessions yet. Add workouts to client training plans to get started.
            </p>
          )}
        </SectionCard>

        {/* Session Review Panel */}
        {reviewSessionId && (
          <SessionReviewPanel
            isOpen={!!reviewSessionId}
            onClose={() => setReviewSessionId(null)}
            sessionId={reviewSessionId}
          />
        )}
      </div>
    </div>
  );
}
